import { genId } from "@smartsheet/lodestar-utils";
import { EventHandler, SyntheticEvent } from "react";
import { ThemeColorNames } from "../avatar/avatarTheme";
import { ursaColorKeys } from "../avatar/avatarUtils";
import { ChipColorVariant } from "../chip/types";
import { BaseOptionType } from "./types";

export const internalOptionToString = <Option extends BaseOptionType>(item: Option | null | undefined): string => {
    if (
        item === null ||
        item === undefined ||
        isCreatableMarkerOption(item) ||
        isSelectAllMarkerOption(item) ||
        isSelectV2OptionSeparator(item)
    ) {
        return "";
    } else if (Object.prototype.hasOwnProperty.call(item, "label")) {
        return item.label as string;
    } else if (Object.prototype.hasOwnProperty.call(item, "name")) {
        return item.name as string;
    } else {
        if (process.env.NODE_ENV !== "production") {
            console.warn(
                "The options provided to the Select or MultiSelect component do not have a label" +
                    " property. In this case, you must provide a the `optionToString` prop.",
                item
            );
        }
        return "";
    }
};

export const creatableMarkerOption = {
    value: "lodestar-select-createable-marker",
};

export const isCreatableMarkerOption = (option: unknown): option is typeof creatableMarkerOption =>
    option === creatableMarkerOption;

export const selectAllMarkerOption = {
    value: "lodestar-multiselect-select-all-marker",
};

export const isSelectAllMarkerOption = (option: unknown): option is typeof selectAllMarkerOption =>
    option === selectAllMarkerOption;

export const selectV2OptionSeparator = {
    value: "lodestar-select-separator-marker",
};

export const isSelectV2OptionSeparator = (option: unknown): option is typeof selectV2OptionSeparator =>
    option === selectV2OptionSeparator;

/**
 * We do this a lot in this component to make sure the container isn't handling
 * click events more than it should.
 */
export const stopPropagationHandler: EventHandler<SyntheticEvent> = (e) => {
    e.stopPropagation();
    // Don't want to send the click up to the ClickAwayListener! We have to use the big hammer here
    // since ClickAwayListener adds event handlers at the document level and we want to make sure
    // they never get there since this event handler and the ones from ClickAwayListener are within
    // the same bubbling phase.
    e.nativeEvent.stopImmediatePropagation();
};

export const useSelectIds = (type: "select" | "combobox", providedId: string | undefined) => {
    const idToUse = providedId || genId(type);
    const sharedIdProps = {
        id: idToUse,
        menuId: idToUse + "--menu",
        toggleButtonId: idToUse + "--toggle-button",
    };
    return type === "select"
        ? sharedIdProps
        : {
              ...sharedIdProps,
              inputId: idToUse,
          };
};

// squashes all consecutive separators when options between them get
// filtered out, and removes leading and trailing separators.
export const squashSeparators = <Option extends BaseOptionType>(options: Option[]) => {
    const squashedOptions = [];
    for (const [index, option] of options.entries()) {
        // escape early if we are adding a non-separator option
        if (!isSelectV2OptionSeparator(option)) {
            squashedOptions.push(option);
        } else {
            // only add non-consecutive separators
            const previousOption = options[index - 1];
            if (!isSelectV2OptionSeparator(previousOption) && !!previousOption) squashedOptions.push(option);
        }
    }
    // remove trailing separator after dealing with all consecutive options
    if (isSelectV2OptionSeparator(squashedOptions[squashedOptions.length - 1])) squashedOptions.pop();

    return squashedOptions;
};

/**
 * Used to find the next valid option after a given index
 *
 * isDescending is used to indicate the direction we are indexing towards,
 * eg. either towards the bottom of the list or towards the top
 */
export const findNextValidOptionIndex = <Option extends BaseOptionType>(
    options: Option[],
    startingIndex = 0,
    isDescending = true
) => {
    for (let idx = startingIndex; options[idx]; idx += isDescending ? 1 : -1) {
        if (!isSelectV2OptionSeparator(options[idx])) return idx;
    }
    return undefined;
};

/**
 * The following three functions are all means of sorting the filteredItems array
 * using the fuzzySearchScoreMap. The first function sorts the array by the fuzzy search score (the default of the our usage).
 * The second function sorts the array alphabetically if the fuzzy search score is within a certain fuzzysearch threshold.
 * The third function does the same but using the fuse.js library and its constants.
 * They share code that looks similar but has minor differences so that an abstraction would be
 * Bloating a function with parameters to handle all cases would be more confusing than helpful
 */

/**
 * sorts the filteredItems and mutates the array and returns it
 */
export const sortFilteredItems = <Option extends BaseOptionType>(
    fuzzySearchScoreMap: Map<Option, number>,
    filteredItems: Option[]
): Option[] => {
    if (fuzzySearchScoreMap.size > 0) {
        return filteredItems.sort((a, b) => {
            const aScore = fuzzySearchScoreMap.get(a);
            const bScore = fuzzySearchScoreMap.get(b);
            if (aScore && bScore) {
                return bScore - aScore;
            }
            return 0;
        });
    }
    return filteredItems;
};

export const sortFilteredItemsAlphabetically = <Option extends BaseOptionType>(
    fuzzySearchScoreMap: Map<Option, number>,
    filteredItems: Option[],
    fuzzyDeltaThreshold = 0.03
) => {
    if (fuzzySearchScoreMap.size > 0) {
        return filteredItems.sort((a, b) => {
            const aScore = fuzzySearchScoreMap.get(a);
            const bScore = fuzzySearchScoreMap.get(b);
            if (aScore && bScore) {
                const scoreDelta = bScore - aScore;
                // the default .03 is significant in that it is the smallest delta found in the
                // words that shouldn't be alphabetized.
                // It was deduced from a set of similar strings' fuzzy score comparisons.
                // this degree of delta is the difference between, for example, Cantaloupe and Carambola
                // in which case Cantaloupe should come first. However, it has a lower fuzzy score
                // than Carambola, when inputing "C" into the fuzzy search.
                const shouldBeAlphabetized = Math.abs(scoreDelta) <= fuzzyDeltaThreshold;
                if (aScore === bScore || shouldBeAlphabetized) {
                    const aString = internalOptionToString(a);
                    const bString = internalOptionToString(b);
                    return aString.localeCompare(bString);
                }
                return bScore - aScore;
            }
            return 0;
        });
    }
    return filteredItems;
};

export const sortFuseFilteredItemsAlphabetically = <Option extends BaseOptionType>(
    fuzzySearchScoreMap: Map<Option, number>,
    filteredItems: Option[],
    fuseDeltaThreshold = 0.0009
): Option[] => {
    if (fuzzySearchScoreMap.size > 0) {
        return filteredItems.sort((a, b) => {
            const aScore = fuzzySearchScoreMap.get(a);
            const bScore = fuzzySearchScoreMap.get(b);
            if (aScore && bScore) {
                const scoreDelta = aScore - bScore;
                // the fuseDeltaThreshold of .0009 is significant in that it is the largest delta found
                // between scores that shouldn't be alphabetized, relying of fuzzy search orderings.
                // It was deduced from a set of similar strings' fuzzy score comparisons.
                // this degree of delta is the difference between, for example, Cantaloupe and Carambola
                // in which case Cantaloupe should come first. However, it has a higher fuzzy score
                // than Carambola, when inputing "C" into the fuzzy search. So in those
                // close cases we should sort alphabetically. This default can be overriden with the parameter.
                const shouldBeAlphabetized = Math.abs(scoreDelta) <= fuseDeltaThreshold;
                if (aScore === bScore || shouldBeAlphabetized) {
                    const aString = internalOptionToString(a);
                    const bString = internalOptionToString(b);
                    return aString.localeCompare(bString);
                }
                return aScore - bScore;
            }
            return 1;
        });
    }
    return filteredItems;
};

// Checking if color is one of the allowed colors from the theme
// Defaulting to the neutral color if the color isn't valid
export const getChipColor = (color: string | undefined): ChipColorVariant => {
    return color === "neutral" || ursaColorKeys.includes(color as ThemeColorNames)
        ? (color as ChipColorVariant)
        : "neutral";
};
